<template>
    <div class="w-full flex flex-col">
        <!-- 标题与说明 -->
        <div class="w-full flex items-end justify-between my-16">
            <div class="text-16 font-bold text-black-primary">区域投诉数据</div>
            <div class="flex items-center" @click="ruleDialogShow = true">
                <img src="@/assets/complaint/ic_info_tips.png" alt="" class="size-14 mr-3">
                <span>统计说明</span>
            </div>
        </div>

        <!-- 数据展示区 -->
        <div class="card w-full relative">
            <!-- 数据加载状态 -->
            <div v-if="loading" class="state-text flex items-center justify-center">
                <div>加载中...</div>
            </div>

            <template v-else>
                <!-- 地图不可用提示（self 本级区域） -->
                <div v-if="!canShowMap && !tableMode" class="state-text flex items-center justify-center">
                    <div>该区域地图无法展示</div>
                </div>

                <!-- 图表模式 -->
                <div v-else-if="!tableMode" class="w-full flex flex-col">
                    <div ref="chartContainer" class="area-map"></div>
                    <div class="flex flex-col w-120 gap-4 ml-24 mb-24">
                        <div>投诉件数量</div>
                        <div class="track"></div>
                        <div class="flex justify-between"><span>多</span><span>少</span></div>
                    </div>
                </div>

                <!-- 表格模式 -->
                <div v-else class="w-full p-10 pb-20">
                    <o-table :columns="tableColumns" :data="datas" :height="330" />
                </div>
            </template>
        </div>

        <!-- 切换按钮 -->
        <div class="w-full flex justify-center items-center my-13" @click="toggleMode">
            <img src="@/assets/complaint/ic_exchange.png" class="size-12 mr-4">
            <span class="text-[#229F60]">{{ tableMode ? '切换为图表' : '切换为表格' }}</span>
        </div>

        <TipsDialog v-model="ruleDialogShow" title="统计说明" :content="ruleText" confirmButtonText="关闭" />
    </div>
</template>

<script>
import { defineComponent, ref, watch, nextTick } from 'vue';
import echarts from "echarts";
import APIS from "@/service/modules/complaintApis/modules/visualization.js";
import { Table } from "@/components/Table";
import TipsDialog from "../../comps/TipsDialog.vue";

// 导入自定义 Hooks
import { useChart } from '../../composables/useChart';
import { useVisualFilter } from '../../composables/useVisualFilter';
import { useAreaInfo } from '../../composables/useAreaInfo';

// GeoJSON 上下文
const geoJsonContext = require.context(
    "../../../../../../assets/geoJson/map/city",
    false,
    /\.json$/
);

export default defineComponent({
    name: "AreaCell",
    components: {
        'o-table': Table,
        TipsDialog,
    },
    props: {
        county: { type: String, default: "" }
    },
    setup(props) {
        // 1. 状态定义
        const loading = ref(false);
        const datas = ref([]);
        const tableMode = ref(false);
        const canShowMap = ref(true);
        const ruleDialogShow = ref(false);

        const { getAreaInfo, getGeoJsonKey } = useAreaInfo();

        const tableColumns = [
            { title: "排序", dataProp: "sort", minWidth: '64px' },
            { title: "区域", dataProp: "cityName", minWidth: '110px' },
            {
                title: "投诉量", dataProp: "total", minWidth: '110px',
                renderCell: (h, { rowData }) => h('div', rowData.total + '件')
            },
            { title: "占比", dataProp: "ratio", minWidth: '90px' },
        ];

        const ruleText = "因存在拆单到不同区域的投诉件，统计时存在以下情形：\n1.累计投诉量≤表格中各区域投诉量之和。例子：投诉件拆单到官渡区、五华区，则列表中各区县投诉件之和为2，累计投诉件为昆明市总的投诉量，仅1件。\n2.投诉量占比=（辖区内投诉量/累计投诉量）*100%，可能存在表格中各区域投诉占比之和≥100%。";

        /**
         * 核心：构建图表配置项（模仿 PC 端优雅封装）
         */
        const buildChartOption = async () => {
            const areaInfo = getAreaInfo(filterParams.value.areaId);
            const areaCode = areaInfo.code;
            const key = getGeoJsonKey(areaCode);

            let geoJson;
            try {
                geoJson = JSON.parse(JSON.stringify(geoJsonContext(`./${key}.json`)));
            } catch (e) {
                console.error(`Map not found for key: ${key}`, e);
                canShowMap.value = false;
                tableMode.value = true;
                return null;
            }

            // 区县过滤逻辑
            if (key !== "yunnan" && !areaCode.endsWith("00")) {
                const childFeature = geoJson.features.find(f => String(f.properties.adcode) === areaCode);
                if (childFeature) geoJson.features = [childFeature];
            }

            // 注册地图 (增加唯一识别码防止污染)
            const registeredName = `map_${key}_${areaCode}`;
            echarts.registerMap(registeredName, geoJson);

            // 准备数据映射
            const dataByCode = {};
            datas.value.forEach(item => {
                const code = item.regionCode || item.adcode || item.areaCode;
                if (code) dataByCode[String(code)] = item;
            });

            const seriesData = geoJson.features.map(f => {
                const adcode = String(f.properties.adcode);
                const item = dataByCode[adcode] || {};
                return {
                    name: f.properties.name,
                    value: Number(item.total || 0),
                    ...item
                };
            });

            const maxValue = seriesData.reduce((max, it) => Math.max(max, it.value), 0);

            return {
                backgroundColor: "#fff",
                tooltip: {
                    trigger: "item",
                    confine: true,
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    padding: [8, 12],
                    borderRadius: 8,
                    extraCssText: 'z-index: 99; box-shadow: 0 4px 12px rgba(0,0,0,0.1);',
                    formatter: (p) => {
                        const d = p.data || {};
                        if (!d.cityName && !p.name) return "";
                        const rankInfo = d.rank && d.rank !== -1 ? `<span style="float:right;margin-left:32px;color:#666">排名 ${d.rank}</span>` : "";
                        return `
                            <div style="min-width:140px">
                                <div style="margin-bottom:4px;line-height:24px">
                                    <span style="font-size:16px;font-weight:bold;color:#333">${d.cityName || p.name}</span>
                                    ${rankInfo}
                                </div>
                                <div style="font-size:12px;color:#999;margin-bottom:4px">投诉量：<span style="color:#666">${formatNumber(d.total || p.value || 0)}</span> 件</div>
                                <div style="font-size:12px;color:#999">占比：<span style="color:#666">${d.ratio || '0.00%'}</span></div>
                            </div>
                        `;
                    }
                },
                visualMap: {
                    show: false,
                    min: 0,
                    max: maxValue || 1,
                    inRange: { color: ["#DAE6FE", "#467EF6"] }
                },
                series: [{
                    type: "map",
                    mapType: registeredName,
                    roam: false,
                    label: { show: false, textStyle: { color: "#1F2D3D", fontSize: 12 } },
                    itemStyle: { normal: { areaColor: "#DAE6FE", borderColor: "#fff" }, emphasis: { areaColor: "#ffdf55" } },
                    data: seriesData
                }]
            };
        };

        // 2. 初始化 Hooks — watch(chartContainer) 会在 DOM 出现时自动 init 并调用 buildChartOption
        const { chartContainer, resetOption } = useChart(buildChartOption);

        const onLoad = async () => {
            loading.value = true;
            datas.value = [];
            const { areaId, beginTime, endTime, rule, channel } = filterParams.value;

            // 判断是否为「本级」区域（self 前缀），本级没有下级地理分布图
            const isSelfArea = String(areaId || '').startsWith('self');
            canShowMap.value = !isSelfArea;
            // self 区域切换时，退出表格模式以展示"该区域地图无法展示"提示
            if (isSelfArea) {
                tableMode.value = false;
            }

            try {
                const res = await APIS.regionCount({
                    cityId: areaId,
                    county: props.county,
                    startDate: beginTime,
                    endDate: endTime,
                    rule: rule,
                    channel: channel,
                });

                if (res.ret === 0) {
                    datas.value = (res.data || []).map(item => ({
                        ...item,
                        name: item.allName,
                        value: item.total,
                    }));
                }
            } catch (err) {
                console.error('Fetch Data Error:', err);
            } finally {
                loading.value = false;
                // 数据刷新后，如果地图可见，用 resetOption 更新图表
                if (canShowMap.value && !tableMode.value) {
                    nextTick(() => resetOption(buildChartOption));
                }
            }
        };

        const { filterParams } = useVisualFilter(onLoad);

        // 3. 辅助方法
        const toggleMode = () => {
            if (!tableMode.value) {
                // 从图表/提示 -> 表格
                tableMode.value = true;
            } else {
                // 从表格 -> 图表/提示
                tableMode.value = false;
                // watch(chartContainer) 会在 DOM 出现时自动初始化图表，无需手动调用
            }
        };

        const formatNumber = (n) => {
            if (!n && n !== 0) return "0";
            return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        };

        return {
            loading,
            datas,
            tableMode,
            canShowMap,
            ruleDialogShow,
            ruleText,
            tableColumns,
            chartContainer,
            toggleMode,
            onLoad
        };
    }
});
</script>

<style lang="less" scoped>
.area-map {
    width: 100%;
    height: 260px;
}

.state-text {
    width: 100%;
    height: 260px;
    color: #999;
    font-size: 14px;
}

.card {
    margin-bottom: 16px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.04);
}

.track {
    width: 100%;
    height: 10px;
    background: linear-gradient(270deg, #dae6fe, #467ef6);
}
</style>
